import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ProfileSort } from '@shared/schemas';

import type { FilterGroup } from '@/types/filters';

import type { ProfilesPage } from '../infrastructure/profiles.gateway';
import { emptyDeactivate, initialState } from './profiles.state';

/**
 * Admin Profiles list slice. Reducers are pure state transitions only; the
 * paginated fetch and the bulk deactivate live in the saga, which reads
 * `page` / `pageSize` / `filters` back from this state and dispatches these
 * transitions as it progresses. `filtersSet` / `pageSizeSet` / `sortSet` reset to
 * the first page (mirroring the old `useInfiniteApi`); the saga triggers the refetch.
 */
const slice = createSlice({
  name: 'profiles',
  initialState,
  reducers: {
    // ── Cursor + filters (the saga refetches after applying these) ──
    pageSet: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    pageSizeSet: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 0;
    },
    filtersSet: (state, action: PayloadAction<FilterGroup | null>) => {
      state.filters = action.payload;
      state.page = 0;
    },
    sortSet: (state, action: PayloadAction<ProfileSort | null>) => {
      state.sort = action.payload;
      state.page = 0;
    },

    // ── Load lifecycle ──
    loadStarted: (state) => {
      state.loading = true;
      state.error = null;
    },
    loadSucceeded: (state, action: PayloadAction<ProfilesPage>) => {
      state.rows = action.payload.rows;
      state.total = action.payload.total;
      state.loading = false;
    },
    loadFailed: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // ── Bulk deactivate lifecycle ──
    deactivateStarted: (state) => {
      state.deactivate = { status: 'deactivating', count: 0, error: null };
    },
    deactivateSucceeded: (state, action: PayloadAction<number>) => {
      state.deactivate = { status: 'succeeded', count: action.payload, error: null };
    },
    deactivateFailed: (state, action: PayloadAction<string>) => {
      state.deactivate = { status: 'failed', count: 0, error: action.payload };
    },
    /** Acknowledge a settled deactivation (page calls after the snackbar). */
    deactivateCleared: (state) => {
      state.deactivate = emptyDeactivate;
    },
  },
});

export const profilesActions = slice.actions;
export const profilesReducer = slice.reducer;
